import * as autobind from 'autobind';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { Button, Container, Form, Message, Modal, Dimmer, Loader } from 'semantic-ui-react';

import { CreateMachineModelDto, NewMachineModelStore } from '../../../../../stores/configuration/machinery/machine-models-store';
import { ItemReference } from '../../../../../stores/dataStore';
import { nameof } from '../../../../../utils/object';
import EquipmentTypeEditor, { EquipmentTypeItemReference } from '../../../../../widgets/bussiness/equipment-type-editor';
import MRClusterEditor from '../../../../../widgets/bussiness/mrcluster-editor';
import OemEditor from '../../../../../widgets/bussiness/oem-editor';

interface NewMachineModelViewProps extends WithTranslation {
  onClose: (isSuccess: boolean) => void;
  newMachineModel?: NewMachineModelStore;
}

// tslint:disable-next-line:no-empty-interface
interface NewMachineModelViewState {
  cluster: ItemReference;
  equipmentType: ItemReference;
  oem: ItemReference;
  userClickedSaved: boolean;
}

@connect(['newMachineModel', NewMachineModelStore])
class NewMachineModelView extends React.Component<NewMachineModelViewProps, NewMachineModelViewState> {
  private get newMachineModelStore() {
    return this.props.newMachineModel;
  }

  constructor(props: NewMachineModelViewProps) {
    super(props);

    this.state = {
      cluster: null,
      equipmentType: null,
      oem: null,
      userClickedSaved: false
    };

    this.newMachineModelStore.createNew({
      name: '',
      clusterId: '',
      equipmentTypeId: '',
      oemId: '',
      description: '',
      active: true
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps: NewMachineModelViewProps) {
    if (this.newMachineModelStore.state.result && this.newMachineModelStore.state.result.isSuccess) {
      nextProps.onClose(true);
    }
  }

  @autobind
  private async onCreateNewItem() {
    this.setState({ userClickedSaved: true });
    this.newMachineModelStore.submit();
  }

  private updateDescription(equipmentType: any, oem: any) {
    this.newMachineModelStore.state.item.description = (equipmentType ? equipmentType.title : '') + ' ' + (oem ? oem.title : '');
  }

  @autobind
  private onCancelNewItem() {
    this.newMachineModelStore.clear();
    this.props.onClose(false);
  }

  @autobind
  private handleValue(property: string, value: any) {
    this.setState({ userClickedSaved: false });
    const change = {};
    change[property] = value;
    this.newMachineModelStore.change({ ...this.newMachineModelStore.state.item, ...change });
  }

  public render() {
    const { t } = this.props as any;

    return (
      <Modal open closeOnEscape={true} onClose={this.onCancelNewItem} closeOnDimmerClick={false}>
        <Dimmer active={this.newMachineModelStore.state.isBusy} style={{ background: 'rgba(0, 0, 0, 0.4)' }}>
          <Loader indeterminate>{t('')}</Loader>
        </Dimmer>
        <Modal.Header className="modal__header">{t('New Machine Model')}</Modal.Header>
        <Modal.Content image>
          <Container>
            {this.newMachineModelStore.state.result && !this.newMachineModelStore.state.result.isSuccess && (
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.newMachineModelStore.state.result.messages.map(o => {
                  return o.body === 'Machine Model already existswith the same name but different EQ Type-OEM relation.'
                    ? 'Machine Model already exists with the same name and same EQ Type-OEM relation.'
                    : o.body;
                })}
              />
            )}
            {this.newMachineModelStore.state.item && (
              <Form>
                <Form.Field required width={8} error={!this.newMachineModelStore.state.item.clusterId && this.state.userClickedSaved}>
                  <div className={`required field`}>
                    <label>{t('Cluster')}</label>
                    <MRClusterEditor
                      placeholder={t('Cluster')}
                      nullable
                      value={this.state.cluster}
                      onChange={c => {
                        this.newMachineModelStore.state.item.clusterId = c ? c.id : null;
                        this.setState({ cluster: c, equipmentType: undefined, userClickedSaved: false });
                      }}
                    />
                  </div>
                </Form.Field>

                <Form.Field required width={8} error={!this.newMachineModelStore.state.item.equipmentTypeId && this.state.userClickedSaved}>
                  <div className={`required field`}>
                    <label>{t('Equipment Type')}</label>
                    <EquipmentTypeEditor
                      placeholder={t('Equipment Type')}
                      nullable
                      clusterId={this.state.cluster ? this.state.cluster.id : undefined}
                      value={this.state.equipmentType}
                      onChange={(c: EquipmentTypeItemReference) => {
                        this.newMachineModelStore.state.item.equipmentTypeId = c ? c.id : null;
                        if (!this.state.cluster) {
                          this.setState({ cluster: c.cluster });
                          this.newMachineModelStore.state.item.clusterId = c.cluster.id;
                        }
                        this.setState({ equipmentType: c, userClickedSaved: false });
                        this.updateDescription(c, this.state.oem);
                      }}
                    />
                  </div>
                </Form.Field>

                <Form.Field required width={8} error={!this.newMachineModelStore.state.item.oemId && this.state.userClickedSaved}>
                  <div className={`required field`}>
                    <label>{t('OEM')}</label>
                    <OemEditor
                      placeholder={t('OEM')}
                      nullable
                      value={this.state.oem}
                      onChange={c => {
                        this.newMachineModelStore.state.item.oemId = c ? c.id : null;
                        this.setState({ oem: c, userClickedSaved: false });
                        this.updateDescription(this.state.equipmentType, c);
                      }}
                    />
                  </div>
                </Form.Field>

                <Form.Field required width={8} error={!this.newMachineModelStore.state.item.name && this.state.userClickedSaved}>
                  <div className={`required field`}>
                    <Form.Input
                      required
                      fluid
                      label={t('Machine Model')}
                      placeholder={t('Machine Model')}
                      value={this.newMachineModelStore.state.item.name}
                      onChange={(e, { value }) => this.handleValue(nameof<CreateMachineModelDto>('name'), value)}
                    />
                  </div>
                </Form.Field>

                <Form.Field required width={8} error={!this.newMachineModelStore.state.item.description && this.state.userClickedSaved}>
                  <div className={`required field`}>
                    <Form.Input
                      required
                      fluid
                      label={t('Description')}
                      placeholder={t('Description')}
                      value={this.newMachineModelStore.state.item.description}
                      onChange={(e, { value }) => this.handleValue(nameof<CreateMachineModelDto>('description'), value)}
                    />
                  </div>
                </Form.Field>

                <Form.Checkbox
                  toggle
                  label={t('Active')}
                  checked={this.newMachineModelStore.state.item.active}
                  placeholder={t('Active')}
                  onChange={(e, { checked }) => this.handleValue(nameof<CreateMachineModelDto>('active'), checked)}
                />
              </Form>
            )}
          </Container>
        </Modal.Content>
        <Modal.Actions>
          <Button className="basic" onClick={this.onCancelNewItem}>
            {t('Cancel')}
          </Button>
          <Button onClick={this.onCreateNewItem} positive>
            {t('Save')}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(NewMachineModelView);
